import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { brandLogo, brandLogoSvg } from '../../../components/brandLogo';
import { products, type ProductId } from '../../../presentation/catalogue';
import { seeded } from '../../../components/random';
import { draw, pulse } from '../../../components/svg';
import { chars, words, revealWords, settleWords } from '../../../components/text';

export interface BranchStory {
  product: ProductId;
  /** Domain concepts, not feature claims (see the product's content file). */
  concepts: string[];
  /** Step 0 and step 1 statements. */
  problem: [string, string];
  /** Optional closing pair shown on a final step. */
  closing?: [string, string];
  seed: number;
}

const CORE = { x: 800, y: 420, r: 70 };
const NODE = { x: 1220, y: 420 };
// Siblings sit above the core, clear of the concepts (left) and the new branch and its name (right).
const SIBLING_ANGLES = [-62, -104, -140];

/**
 * A MINT specialisation told at the domain level: separate domain concepts connect into the MINT
 * core, and the branch for this domain activates. Used where the product documentation confirms
 * the positioning but not detailed features (MINT Travel & Events, MINT Business).
 *
 * 0 Domain concepts, separate — problem[0]
 * 1 They connect into MINT — problem[1]
 * 2 The branch activates: the product is named
 * 3 (optional) closing pair
 */
export abstract class MintBranchScene extends BaseScene {
  protected abstract readonly story: BranchStory;
  private flowing = false;

  public get stepCount(): number {
    return this.story.closing ? 4 : 3;
  }

  private positions(): Array<{ x: number; y: number }> {
    const random = seeded(this.story.seed);
    return this.story.concepts.map((_, i) => ({
      x: 150 + (i % 2) * 190 + random() * 90,
      y: 200 + i * (480 / Math.max(this.story.concepts.length - 1, 1)) + (random() - 0.5) * 30,
    }));
  }

  protected template(): string {
    const product = products[this.story.product];
    const spots = this.positions();
    const concepts = this.story.concepts.map((c, i) => {
      const p = spots[i];
      return `<g class="mb-concept" data-index="${i}">
        <path class="mb-concept__link draw" pathLength="1" d="M${p.x + 70} ${p.y} C${(p.x + CORE.x) / 2 + 60} ${p.y} ${CORE.x - 180} ${CORE.y} ${CORE.x - CORE.r - 8} ${CORE.y}"/>
        <g transform="translate(${p.x.toFixed(1)} ${p.y.toFixed(1)})"><g class="mb-concept__chip" style="--d:${(4 + (i % 3)).toFixed(1)}s;--delay:${(-i * 0.6).toFixed(1)}s">
          <rect x="-72" y="-19" width="144" height="38" rx="19"/>
          <text y="6" text-anchor="middle">${c.toUpperCase()}</text>
        </g></g>
      </g>`;
    }).join('');

    // Other enabled specialisations stay visible in the background, so the family is never lost.
    const siblings = this.context.products.filter((p) => p.tier === 'specialisation' && p.id !== this.story.product);
    const siblingMarkup = siblings.map((s, i) => {
      const a = (SIBLING_ANGLES[i % SIBLING_ANGLES.length] * Math.PI) / 180;
      const x = CORE.x + Math.cos(a) * 190;
      const y = CORE.y + Math.sin(a) * 190;
      return `<g class="mb-sibling">
        <path d="M${(CORE.x + Math.cos(a) * (CORE.r + 8)).toFixed(1)} ${(CORE.y + Math.sin(a) * (CORE.r + 8)).toFixed(1)} L${x.toFixed(1)} ${y.toFixed(1)}"/>
        <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6"/>
        <text x="${(x + (x < CORE.x ? -14 : 14)).toFixed(1)}" y="${(y + 5).toFixed(1)}" text-anchor="${x < CORE.x ? 'end' : 'start'}">${s.name}</text>
      </g>`;
    }).join('');

    const pulses = this.story.concepts.map(() => '<circle class="mb-pulse" r="4"/>').join('');
    const [p0, p1] = this.story.problem;
    const closing = this.story.closing
      ? `<div class="mb-closing" data-from="3">${this.story.closing.map((l, i) => `<h2 class="statement${i === 1 ? ' statement--accent' : ''}">${words(l)}</h2>`).join('')}</div>`
      : '';

    return `
      <div class="scene-layout mint-branch">
        <svg class="diagram mb-diagram" viewBox="0 0 1600 900" role="img" aria-label="${product.name}: domain context connected through MINT">
          ${siblingMarkup}
          ${concepts}
          <path class="mb-branch draw" data-from="2" pathLength="1" d="M${CORE.x + CORE.r + 8} ${CORE.y} H${NODE.x - 50}"/>
          <g class="mb-core">
            <circle class="mb-core__glow" cx="${CORE.x}" cy="${CORE.y}" r="${CORE.r + 36}"/>
            <circle class="mb-core__ring" cx="${CORE.x}" cy="${CORE.y}" r="${CORE.r}"/>
            ${brandLogoSvg('mint', { x: CORE.x, y: CORE.y, height: 28, surface: 'stage', className: 'mb-core__label' })}
          </g>
          <g class="mb-node" data-from="2">
            <circle class="mb-node__halo" cx="${NODE.x}" cy="${NODE.y}" r="46"/>
            <circle class="mb-node__dot" cx="${NODE.x}" cy="${NODE.y}" r="14"/>
          </g>
          <g class="mb-pulses">${pulses}</g>
        </svg>
        <div class="mb-name" data-from="2" style="--family:${this.meta.accent}">
          <span class="product-reveal__parent">${brandLogo('mint', { height: 24 })}</span>
          <h2 class="mb-name__title" aria-label="${product.name}">${chars(product.name)}</h2>
          <p class="mb-name__tagline">${product.tagline}</p>
        </div>
        <div class="mb-statements">
          <h2 class="statement" data-from="0" data-until="0">${words(p0)}</h2>
          <h2 class="statement statement--accent" data-from="1" data-until="1">${words(p1)}</h2>
          ${closing}
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$$('.mb-concept__chip'), { opacity: 0, scale: 0.8, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 0.6, stagger: 0.1, ease: 'back.out(1.8)' }, 0.2);
    tl.add(revealWords(this.$('.mb-statements .statement')), 0.8);
  }

  protected onExit(): void {
    this.flowing = false;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    gsap.set(this.$$('.mb-concept__chip'), { opacity: 1, scale: 1 });
    this.$$('.mb-statements .statement').forEach((s) => settleWords(s));
    this.$('.mb-diagram').dataset.connected = String(step >= 1);

    // 1 — domain context connects into MINT.
    const links = this.$$('.mb-concept__link');
    if (forward && step === 1) {
      draw(links, false, { immediate: true });
      tl.add(draw(links, true, { duration: 1, stagger: 0.1, ease: 'power2.inOut' }), 0.2);
      tl.add(revealWords(this.$$('.mb-statements .statement')[1]), 0.1);
    } else {
      draw(links, step >= 1, { immediate: true });
    }

    // 2 — the branch for this domain activates and the product is named.
    const branch = this.$('.mb-branch');
    const node = this.$$('.mb-node circle');
    const letters = this.$$('.mb-name__title .char');
    const extras = this.$$('.mb-name .product-reveal__family, .mb-name__tagline');
    if (forward && step === 2) {
      draw(branch, false, { immediate: true });
      tl.add(draw(branch, true, { duration: 0.9, ease: 'power2.out' }), 0.2);
      tl.fromTo(node, { scale: 0, transformOrigin: '50% 50%' }, { scale: 1, duration: 0.7, ease: 'back.out(2.2)' }, 0.9);
      tl.fromTo(letters, { opacity: 0, filter: 'blur(10px)', y: 12 }, { opacity: 1, filter: 'blur(0px)', y: 0, duration: 1, stagger: { each: 0.035, from: 'start' }, ease: 'power3.out' }, 1.1);
      tl.fromTo(extras, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.7, stagger: 0.2 }, 1.4);
    } else {
      draw(branch, step >= 2, { immediate: true });
      gsap.set(node, { scale: 1, transformOrigin: '50% 50%' });
      gsap.set([...letters, ...extras], { opacity: 1, filter: 'blur(0px)', y: 0 });
    }

    // Concepts settle back once they have handed their context to MINT.
    gsap.to(this.$$('.mb-concept'), { opacity: step >= 2 ? 0.45 : 1, duration: d * 0.8 });
    gsap.to(this.$('.mb-diagram'), { opacity: step >= 3 ? 0.14 : 1, duration: d * 0.8 });
    gsap.to(this.$('.mb-name'), { opacity: step >= 3 ? 0.06 : 1, duration: d * 0.8 });
    if (forward && step === 3) this.$$('.mb-closing .statement').forEach((s, i) => tl.add(revealWords(s), 0.3 + i * 0.6));

    this.syncFlow(step >= 1, forward && step === 1);
  }

  private syncFlow(on: boolean, delayed: boolean): void {
    if (on === this.flowing) return;
    this.flowing = on;
    if (!on) {
      this.stopLoops();
      this.$$('.mb-pulse').forEach((m) => { m.style.opacity = '0'; });
      return;
    }
    const links = this.$$<SVGPathElement>('.mb-concept__link');
    this.$$<SVGCircleElement>('.mb-pulse').forEach((marker, i) => {
      this.loop(pulse(marker, links[i], { duration: 1.4 + (i % 3) * 0.3, delay: (delayed ? 1.2 : 0) + i * 0.25, repeatDelay: 0.8 + (i % 2) * 0.6 }));
    });
  }
}
